/**
 * Shared "named preset with a sticky Custom fallback" logic.
 *
 * Both the ranking-weight strategy presets (vespa-strategy-presets.tsx) and
 * the Content Window presets (vespa-content-window.tsx) follow the same
 * pattern: a fixed list of named presets, plus a reflected "Custom" selection
 * that has no value of its own and must be tracked as explicit state (not
 * re-derived every render) so clicking Custom is a real state transition even
 * when the current value already happens to match a preset exactly.
 *
 * This module factors out the one genuinely shared piece — matching a current
 * value against a list of presets via a caller-provided equality check, with a
 * shared CUSTOM_PRESET sentinel — while leaving each domain's own compare
 * logic (numeric weight-pair equality vs. unit-normalized seconds equality)
 * and UI wiring (ChoiceSelect vs. Segmented) in their own files, since those
 * differ enough that folding them into one generic component wouldn't
 * meaningfully reduce complexity.
 */

/** A reflected, non-applicable selection shared by both preset systems. */
export const CUSTOM_PRESET = 'custom'

/**
 * Finds the preset (if any) whose value matches `current` under `isMatch`.
 * Returns CUSTOM_PRESET when nothing matches — the caller then decides
 * whether to auto-snap the selection to that (e.g. on a hand-typed value) or
 * to leave a previously-explicit Custom selection alone (e.g. on an
 * unrelated field's re-render).
 */
export function detectPreset<TKey extends string, TValue>(
    presets: Record<TKey, TValue> | ReadonlyArray<{ key: TKey; value: TValue }>,
    current: TValue,
    isMatch: (presetValue: TValue, current: TValue) => boolean,
): TKey | typeof CUSTOM_PRESET {
    const entries = Array.isArray(presets)
        ? presets.map((p) => [p.key, p.value] as const)
        : (Object.entries(presets) as [TKey, TValue][])

    for (const [key, value] of entries) {
        if (isMatch(value, current)) return key
    }
    return CUSTOM_PRESET
}
